import type { RollResult } from '@casino/craps-engine';
import Phaser from 'phaser';
import { showTierUnlockToast } from '../../objects/tierUnlockToast.js';
import type { TableId } from '@casino/core';
import { BackToFloorPill } from '../../components/BackToFloorPill.js';
import { BankrollPill } from '../../components/BankrollPill.js';
import { ShimmerClock } from '../../objects/ShimmerClock.js';
import { Bezel } from '../../components/Bezel.js';
import { PlayerCardPill } from '../../components/PlayerCardPill.js';
import { HelpToggle } from '../../components/HelpToggle.js';
import { PlayerCard } from '../../components/PlayerCard.js';
import { SpectatorPill } from '../../components/SpectatorPill.js';
import { ZoneHelpCard } from '../../components/ZoneHelpCard.js';
import { formatCents } from '../../format.js';
import { computePlayerCardRect } from '../../layout/playerCardRect.js';
import { getPlayerTier, load } from '../../persistence.js';
import { NON_ZONE_HELP, ZONE_META } from '../../layout/zones.js';
import type { ZoneDef } from '../../layout/zones.js';
import { Tooltip } from '../../components/Tooltip.js';
import { TooltipController } from './tooltipController.js';
import type { ResolverState } from './zoneHelpCopy.js';
import { loadPrefs, savePrefs } from '../../prefs.js';

const OVERLAY_DEPTH = 900;
const OVERLAY_CARD_DEPTH = 901;

export class UIScene extends Phaser.Scene {
  shimmerClock?: ShimmerClock | undefined;
  private bankrollPill!: BankrollPill;
  private bankrollCents = 0;
  private overlayCard: PlayerCard | undefined = undefined;
  private overlayBackdrop: Phaser.GameObjects.GameObject[] | undefined = undefined;
  private dpr = 1;
  private floorPill!: BackToFloorPill;
  private spectatorPill: SpectatorPill | null = null;
  // Bezel geometry captured at create() so setSpectatorBanner can place
  // the pill directly under the bezel's curved bottom edge.
  private bezelBottomY = 0;
  private helpToggle!: HelpToggle;
  private helpCard: ZoneHelpCard | undefined = undefined;
  private helpDismissCatcher: Phaser.GameObjects.GameObject | undefined;
  private tooltip: Tooltip | undefined;
  private tooltipController: TooltipController | undefined;
  private hoverCapable = false;
  private engineStateGetter: () => ResolverState = () => ({ phase: 'COMEOUT', point: undefined, bets: [] });

  constructor() {
    super('CrapsUIScene');
  }

  // Phaser preserves the scene class instance across stop/start; class-field
  // references point to destroyed GameObjects from the prior run, but stay
  // JS-truthy. Clear them in init() so external code (the bridge polling for
  // "scene ready") can't latch onto a stale reference before this run's
  // create() reassigns it.
  init() {
    this.bankrollPill = undefined as unknown as BankrollPill;
    this.shimmerClock = undefined;
    this.overlayCard = undefined;
    this.overlayBackdrop = undefined;
    this.bankrollCents = 0;
    this.helpToggle = undefined as unknown as HelpToggle;
    this.helpCard = undefined;
    this.helpDismissCatcher = undefined;
    this.tooltip = undefined;
    this.tooltipController = undefined;
    this.hoverCapable = false;
    this.engineStateGetter = () => ({ phase: 'COMEOUT', point: undefined, bets: [] });
  }

  create() {
    const W = this.scale.width;
    const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
    this.dpr = dpr;

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.shimmerClock?.destroy();
      this.shimmerClock = undefined;
    });

    // History strip removed — kept the setHistory method in case future
    // surfaces want to reuse the data, but no on-screen text.

    // Bankroll: large $ amount centered horizontally, sitting inside a
    // bezel that mirrors the chip rack's bezel at the bottom. Display
    // only — taps go to the dedicated PLAYER CARD pill at the top-right.
    const bezelW = Math.min(W * 0.30, Math.round(180 * dpr));
    const bezelH = Math.round(56 * dpr);
    this.bezelBottomY = bezelH;
    const moneyBezel = new Bezel({
      scene: this,
      centerX: Math.round(W / 2),
      outerY: 0,
      width: bezelW,
      height: bezelH,
      face: 'down',
    });
    this.shimmerClock = new ShimmerClock(this);
    this.bankrollPill = new BankrollPill({
      scene: this,
      centerX: Math.round(W / 2),
      // Sit the text vertically centered inside the bezel slab.
      topY: Math.round(bezelH * 0.18),
      dpr,
      onClick: () => {}, // display only
      // Bezel grows alongside the dollar text on win-pop (dampened curve
      // inside BankrollPill).
      scaleAlong: [moneyBezel.gfx],
      shimmerClock: this.shimmerClock,
    });
    this.bankrollPill.setBankrollCents(this.bankrollCents);

    this.floorPill = new BackToFloorPill({
      scene: this,
      x: Math.round(12 * dpr),
      y: Math.round(14 * dpr),
      scale: dpr,
      onClick: () => this.game.events.emit('leave:request'),
    });

    new PlayerCardPill({
      scene: this,
      rightX: W - Math.round(12 * dpr),
      topY: Math.round(14 * dpr),
      scale: dpr,
      onClick: () => this.toggleBankrollOverlay(),
    });

    this.helpToggle = new HelpToggle({
      scene: this,
      x: Math.round(12 * dpr),
      y: Math.round(46 * dpr), // 32px below FLOOR pill
      scale: dpr,
      onToggle: () => this.toggleHelpMode(),
    });

    this.hoverCapable =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (this.hoverCapable) {
      this.tooltip = new Tooltip(this, { width: Math.min(280 * dpr, this.scale.width * 0.8) });
      this.tooltip.setDepth(900);

      const tooltipController = new TooltipController({
        tooltip: this.tooltip,
        getState: () => this.engineStateGetter(),
        showDelayMs: 120,
      });
      this.tooltipController = tooltipController;

      const table = this.scene.get('CrapsTableScene');
      // Capture handler refs so we can detach them on shutdown. Without this,
      // re-entering the table accumulates dead listeners that still hold the
      // old (destroyed) Tooltip and crash inside Phaser's text rendering.
      const onZoneHover = (def: unknown, anchor: unknown) => {
        tooltipController.onHover(def as never, anchor as never);
      };
      const onZoneHoverEnd = () => {
        tooltipController.onHoverEnd();
      };
      table.events.on('zone:hover', onZoneHover);
      table.events.on('zone:hoverEnd', onZoneHoverEnd);
      this.events.once('shutdown', () => {
        table.events.off('zone:hover', onZoneHover);
        table.events.off('zone:hoverEnd', onZoneHoverEnd);
        // Cancel any in-flight show timer; otherwise the 120ms delay can fire
        // after the Tooltip's Text is destroyed and crash inside Phaser's
        // canvas blit (drawImage on a null context).
        tooltipController.setEnabled(false);
      });

      // Apply persisted pref. Default: tooltips on for desktop.
      const prefs = loadPrefs();
      this.helpToggle.setArmed(prefs.tooltipsEnabled);
      tooltipController.setEnabled(prefs.tooltipsEnabled);
    }
  }

  private toggleBankrollOverlay() {
    if (this.overlayCard) this.closeBankrollOverlay();
    else this.openBankrollOverlay();
  }

  private openBankrollOverlay() {
    // Bring this scene above the dice overlay so the modal isn't covered
    // by mid-throw dice or the resting dice that sit at depth 1000 above
    // every other UI element.
    this.scene.bringToTop();
    const W = this.scale.width;
    const H = this.scale.height;
    const dim = this.add.graphics().setDepth(OVERLAY_DEPTH);
    dim.fillStyle(0x000000, 0.55);
    dim.fillRect(0, 0, W, H);
    const dismiss = this.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0)
      .setInteractive()
      .setDepth(OVERLAY_DEPTH);
    dismiss.on('pointerdown', () => this.closeBankrollOverlay());

    const cardRect = computePlayerCardRect(W, H);

    this.overlayBackdrop = [dim, dismiss];

    this.overlayCard = new PlayerCard({
      scene: this,
      rect: cardRect,
      dpr: this.dpr,
      brandText: 'Casino',
      primaryButton: {
        label: 'TAKE DOWN',
        onClick: () => {
          this.events.emit('takeDown:click');
          this.closeBankrollOverlay();
        },
      },
      secondaryButton: {
        label: 'RESET',
        onClick: () => {
          this.events.emit('reset:click');
          this.closeBankrollOverlay();
        },
      },
      tier: getPlayerTier(load()),
    });
    this.overlayCard.setDepth(OVERLAY_CARD_DEPTH);
    this.overlayCard.setBankrollCents(this.bankrollCents);
    this.bankrollPill.setDepth(OVERLAY_CARD_DEPTH);
    this.bankrollPill.setExpanded(true);
  }

  private closeBankrollOverlay() {
    if (this.overlayBackdrop) {
      for (const o of this.overlayBackdrop) o.destroy();
      this.overlayBackdrop = undefined;
    }
    this.overlayCard?.destroy();
    this.overlayCard = undefined;
    this.bankrollPill.setDepth(0);
    this.bankrollPill.setExpanded(false);
    // Restore the dice overlay above us so the dice fly over UI again.
    this.scene.get('CrapsDiceOverlayScene')?.scene.bringToTop();
  }

  /** Bankroll-pill anchor (top-right) — fly-target for winning chips. */
  getBankrollAnchor(): { x: number; y: number } {
    return this.bankrollPill.getAnchor();
  }

  showTierUnlockToast(tableId: TableId): void {
    const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
    showTierUnlockToast(this, tableId, dpr);
  }

  setBankrollCents(cents: number, opts: { animate?: boolean } = {}) {
    this.bankrollCents = cents;
    // Pill may not exist if the bridge somehow drives the UI before
    // create() finishes. Cache the cents on the field; create() picks it up
    // when constructing the pill.
    this.bankrollPill?.setBankrollCents(cents, opts);
    this.overlayCard?.setBankrollCents(cents);
  }

  setHistory(_history: RollResult[]) {
    // No-op: history strip removed from the UI. Kept the method so the
    // bridge's per-roll setHistory(...) call still typechecks.
  }

  /**
   * Spectator status pill, mounted just under the bankroll bezel. Pass
   * 'live' / 'replay' to show; null to hide. Idempotent — repeated calls
   * with the same mode are no-ops. Only used in spectate mode; the player's
   * bridge never calls this.
   */
  setSpectatorBanner(mode: 'live' | 'replay' | null): void {
    // Swap the FLOOR pill to EXIT in spectate mode — the spectator has no
    // lobby to return to (different Phaser game; no LobbyScene). Tapping
    // EXIT navigates the page back to "/" so the producer's localStorage
    // session is untouched but the spectator viewer is closed.
    if (mode === null) {
      this.spectatorPill?.destroy();
      this.spectatorPill = null;
      this.floorPill.setLabel('◀ FLOOR', () => this.game.events.emit('navigate:lobby'));
      return;
    }
    this.floorPill.setLabel('◀ EXIT', () => { window.location.href = '/'; });
    if (this.spectatorPill) {
      this.spectatorPill.setMode(mode);
      return;
    }
    const dpr = this.dpr;
    this.spectatorPill = new SpectatorPill({
      scene: this,
      centerX: Math.round(this.scale.width / 2),
      // Sit just below the bezel's curved bottom edge so the pill reads
      // as continuing the bezel's silhouette downward.
      topY: this.bezelBottomY + Math.round(6 * dpr),
      scale: dpr,
      initialMode: mode,
    });
  }

  showWinToast(cents: number) {
    if (cents <= 0) return;
    const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
    const sm = Math.min(this.scale.width / 1280, this.scale.height / 800);
    const fpx = (n: number) => Math.round(n * dpr);
    const lpx = (n: number) => Math.round(n * sm);
    const anchor = this.bankrollPill.getAnchor();
    const x = anchor.x;
    const y = anchor.y - lpx(4);
    const toast = this.add
      .text(x, y, `+${formatCents(cents)}`, {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${fpx(16)}px`,
        color: '#ffd24a',
        fontStyle: 'bold',
      })
      .setOrigin(0, 1);
    this.tweens.add({
      targets: toast,
      alpha: { from: 1, to: 0 },
      y: y - lpx(20),
      duration: 2400,
      ease: 'Cubic.In',
      onComplete: () => toast.destroy(),
    });
  }

  private toggleHelpMode() {
    const armed = this.helpToggle.isArmed();
    if (this.hoverCapable) {
      // Desktop: armed = tooltips visible; persist the pref.
      this.tooltipController?.setEnabled(armed);
      savePrefs({ tooltipsEnabled: armed });
      return;
    }
    // Touch: today's modal flow — arm click-to-explain on the table.
    const table = this.scene.get('CrapsTableScene') as import('./TableScene.js').TableScene;
    table.setHelpMode(armed);
    if (!armed) this.closeHelpCard();
  }

  showZoneHelp(def: ZoneDef) {
    const meta = ZONE_META[def.id];
    if (!meta) return;
    this.openHelpCardWith(meta.helpTitle, meta.helpText);
  }

  /** Title/body version used by the shared zoneHelpCopy resolver. */
  openHelp(title: string, body: string) {
    this.openHelpCardWith(title, body);
  }

  /** Called by the bridge to give us a live engine-state getter for tooltip resolution. */
  bindEngineState(getter: () => ResolverState): void {
    this.engineStateGetter = getter;
  }

  showNonZoneHelp(key: keyof typeof NON_ZONE_HELP) {
    const entry = NON_ZONE_HELP[key];
    this.openHelpCardWith(entry.helpTitle, entry.helpText);
  }

  private openHelpCardWith(title: string, body: string) {
    const W = this.scale.width;
    const H = this.scale.height;
    const dpr = this.dpr;
    const cardW = Math.min(Math.round(W * 0.86), Math.round(420 * dpr));
    if (!this.helpCard) {
      this.helpCard = new ZoneHelpCard({
        scene: this,
        cx: W / 2,
        cy: H / 2,
        width: cardW,
        dpr,
        onClose: () => this.closeHelpCard(),
      });
      this.helpCard.setDepth(950);

      // Tap-outside catcher.
      this.helpDismissCatcher = this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0)
        .setInteractive()
        .setDepth(940);
      this.helpDismissCatcher.on('pointerdown', () => this.closeHelpCard());
    }
    this.helpCard.setContent(title, body);
  }

  private closeHelpCard() {
    this.helpCard?.destroy();
    this.helpCard = undefined;
    this.helpDismissCatcher?.destroy();
    this.helpDismissCatcher = undefined;
  }
}
